import React, { useEffect, useMemo, useState, useCallback } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import {
  useFetchProductDateRankRulesMutation,
  useExportProductDateRankRulesMutation,
} from "../../../features/tenant/rankRules/productDateRankRulesApi";
import toast from "react-hot-toast";
import { formatter } from "../../../utils/numberFormatter";

// ── Config ───────────────────────────────────────────────────────────────────
// Hardcoded FY list (add new years here as they roll in)
const FINANCIAL_YEARS = ["2026-2027", "2025-2026"];
const DEFAULT_FY = "2026-2027";

const MONTH_NAMES = [
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March",
];
// FY month order: April = idx 0 ... March = idx 11
// Real calendar month (1-12) for each FY index
const FY_MONTH_TO_CAL = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

// Given an FY string ("2026-2027"), return the calendar year for FY index i
const calYearForFyIndex = (fy, fyIdx) => {
  const [startY] = fy.split("-").map(Number);
  // April(0)–December(8) → startY, January(9)–March(11) → startY + 1
  return fyIdx <= 8 ? startY : startY + 1;
};

// Build list of month options for a given FY
// If it's the current FY, only include months that have started (today or earlier)
const buildMonthOptions = (fy) => {
  const today = new Date();
  const currentCalYear = today.getFullYear();
  const currentCalMonth = today.getMonth() + 1; // 1-12

  const options = [];
  for (let fyIdx = 0; fyIdx < 12; fyIdx++) {
    const calMonth = FY_MONTH_TO_CAL[fyIdx];
    const calYear = calYearForFyIndex(fy, fyIdx);

    // If this month is in the future relative to today → skip
    const isFuture =
      calYear > currentCalYear ||
      (calYear === currentCalYear && calMonth > currentCalMonth);

    if (isFuture) continue;

    options.push({
      value: `${calYear}-${String(calMonth).padStart(2, "0")}`,
      label: `${MONTH_NAMES[fyIdx]} ${calYear}`,
      isCurrent: calYear === currentCalYear && calMonth === currentCalMonth,
    });
  }
  return options;
};

const ProductDateRankRules = () => {
  const [
    fetchProductDateRankRules,
    { data: rulesData, isLoading: rulesIsLoading, error: rulesError },
  ] = useFetchProductDateRankRulesMutation();

  const [exportProductDateRankRules, { isLoading: isExporting }] =
    useExportProductDateRankRulesMutation();

  const [totalRows, setTotalRows] = useState(0);
  const [data, setData] = useState([]);
  const [limit, setLimit] = useState(30);
  const [offset, setOffset] = useState(0);

  // Filters — the ones actually applied (what we send to API)
  const [financialYear, setFinancialYear] = useState(DEFAULT_FY);
  const [month, setMonth] = useState(""); // set after mount to current month
  const [productCodeInput, setProductCodeInput] = useState(""); // what user types
  const [productCode, setProductCode] = useState(""); // debounced value that hits API
  const [status, setStatus] = useState("all");

  // Month options for the currently selected FY
  const monthOptions = useMemo(() => buildMonthOptions(financialYear), [financialYear]);

  // Initialize month to "current" the first time options are ready
  useEffect(() => {
    if (!month && monthOptions.length > 0) {
      const current = monthOptions.find((o) => o.isCurrent) || monthOptions[monthOptions.length - 1];
      setMonth(current.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthOptions]);

  // If FY changes and current month isn't in new options → auto-pick a valid one
  useEffect(() => {
    if (monthOptions.length === 0) {
      setMonth("");
      return;
    }
    const stillValid = monthOptions.some((o) => o.value === month);
    if (!stillValid) {
      const current = monthOptions.find((o) => o.isCurrent) || monthOptions[monthOptions.length - 1];
      setMonth(current.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYear]);

  // Debounce productCode: only trigger API 400ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setProductCode(productCodeInput.trim());
      setOffset(0);
    }, 400);
    return () => clearTimeout(t);
  }, [productCodeInput]);

  // Fetch whenever a filter (or pagination) changes
  const fetchRulesFunc = useCallback(() => {
    if (!month) return; // skip until month initialized
    fetchProductDateRankRules({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      limit,
      offset,
      body: {
        product_code: productCode,
        financial_year: financialYear,
        month,
        status,
        sort_by: "total_sale_value",
        sort_order: "desc",
      },
    });
  }, [fetchProductDateRankRules, limit, offset, productCode, financialYear, month, status]);

  useEffect(() => {
    fetchRulesFunc();
  }, [fetchRulesFunc]);

  useEffect(() => {
    if (rulesData?.body) {
      setData(rulesData.body.data || []);
      setTotalRows(rulesData.body.pagination?.total_rows || 0);
    }
    if (rulesError) {
      toast.error("Something went wrong", { id: "rulesError" });
      console.error("Product date rank rules error:", rulesError);
    }
  }, [rulesData, rulesError]);

  const clearFilters = () => {
    setFinancialYear(DEFAULT_FY);
    setProductCodeInput("");
    setProductCode("");
    setStatus("all");
    setOffset(0);
    // month will auto-reset via FY effect
  };

  const formatDate = useCallback((d) => {
    if (!d) return "N/A";
    try {
      return new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return d;
    }
  }, []);

  // ── Export to Excel ─────────────────────────────────────────────────────
  // Server generates the full (unpaginated) report for the current filters
  // and returns a download link — no client-side file building needed.
  const handleExportExcel = useCallback(async () => {
    try {
      const response = await exportProductDateRankRules({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        body: {
          product_code: productCode,
          financial_year: financialYear,
          month,
          status,
          sort_by: "total_sale_value",
          sort_order: "desc",
        },
      }).unwrap();

      const fileUrl = response?.body?.file_link;

      if (!fileUrl) {
        toast.error("Export succeeded but no file link was returned", { id: "exportError" });
        console.error("Export response missing file_link:", response);
        return;
      }

      // Trigger download
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Report downloaded", { id: "exportSuccess" });
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export data", { id: "exportError" });
    }
  }, [exportProductDateRankRules, productCode, financialYear, month, status]);

  // Columns memoized so DataTable doesn't re-render unnecessarily
  const columns = useMemo(
    () => [
      {
        name: "SNo",
        cell: (row, idx) => offset + Number(idx + 1),
        width: "70px",
      },
      {
        name: "Product Code",
        selector: (row) => row.product_code || "N/A",
        sortable: true,
        width: "180px",
        cell: (row) => (
          <span className="font-semibold text-gray-800">{row.product_code || "N/A"}</span>
        ),
      },
      {
        name: "Total Sale Value",
        selector: (row) => row.total_sale_value || 0,
        sortable: true,
        width: "180px",
        right: true,
        cell: (row) => (
          <span className="font-semibold text-gray-800">
            ₹ {formatter.format(row.total_sale_value || 0)}
          </span>
        ),
      },
      {
        name: "Start Date",
        selector: (row) => row.start_date,
        sortable: true,
        width: "130px",
        cell: (row) => formatDate(row.start_date),
      },
      {
        name: "End Date",
        selector: (row) => row.end_date,
        sortable: true,
        width: "140px",
        cell: (row) =>
          row.is_ongoing ? (
            <span className="text-blue-700 font-medium">Ongoing</span>
          ) : (
            formatDate(row.end_date)
          ),
      },
      {
        name: "Duration",
        selector: (row) => row.duration_days || 0,
        sortable: true,
        width: "110px",
        cell: (row) => (
          <span className="text-gray-600">
            {row.duration_days} {row.duration_days === 1 ? "day" : "days"}
          </span>
        ),
      },
      {
        name: "Rank",
        selector: (row) => row.rank,
        width: "90px",
        center: true,
        cell: (row) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
            {Number(row.rank).toFixed(2)}
          </span>
        ),
      },
      {
        name: "State",
        cell: (row) => {
          const styles = {
            Active: "bg-green-100 text-green-800",
            Ended: "bg-gray-200 text-gray-700",
            Upcoming: "bg-blue-100 text-blue-800",
          };
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                styles[row.state] || "bg-gray-100 text-gray-700"
              }`}
            >
              {row.state}
            </span>
          );
        },
        width: "110px",
      },
      {
        name: "Status",
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
              row.status === "1" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            }`}
          >
            {row.status === "1" ? "Active" : "Inactive"}
          </span>
        ),
        width: "100px",
      },
      {
        name: "Updated",
        selector: (row) => row.updated_at,
        sortable: true,
        width: "140px",
        cell: (row) => <span className="text-gray-600 text-xs">{formatDate(row.updated_at)}</span>,
      },
    ],
    [offset, formatDate]
  );

  const handlePageChange = useCallback(
    (pageNumber) => setOffset((pageNumber - 1) * limit),
    [limit]
  );

  const handlePerRowsChange = useCallback((newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
  }, []);

  const hasActiveFilters =
    productCode || status !== "all" || financialYear !== DEFAULT_FY;

  const selectedMonthLabel = useMemo(
    () => monthOptions.find((o) => o.value === month)?.label || "",
    [monthOptions, month]
  );

  return (
    <div className="flex w-full bg-gray-200 min-h-screen justify-center">
      <div className="w-full">
        {/* Header */}
        <div className="border-b bg-white border-gray-300 pl-6 pr-4 py-2 shadow-sm flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold">High GP Product Rules</span>
            <LinksComponent />
          </div>

          <button
            onClick={handleExportExcel}
            disabled={isExporting || rulesIsLoading}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
            title="Export all filtered data to Excel"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4"
            >
              <path d="M12 3v12" />
              <path d="M7 10l5 5 5-5" />
              <path d="M4 19h16" />
            </svg>
            {isExporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>

        <div className="p-3">
          {/* Filter Card */}
          <div className="bg-white shadow rounded-lg mb-3 border border-gray-200">
            <div className="border-b border-gray-300 bg-sky-50 rounded-t-lg px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Filters</span>
                {hasActiveFilters && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    active
                  </span>
                )}
              </div>
              {selectedMonthLabel && (
                <span className="text-xs text-gray-500">
                  Viewing:&nbsp;
                  <span className="font-semibold text-gray-700">{selectedMonthLabel}</span>
                </span>
              )}
            </div>

            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                {/* Financial Year */}
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                    Financial Year
                  </label>
                  <select
                    className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    value={financialYear}
                    onChange={(e) => {
                      setFinancialYear(e.target.value);
                      setOffset(0);
                    }}
                  >
                    {FINANCIAL_YEARS.map((fy) => (
                      <option key={fy} value={fy}>
                        {fy}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month */}
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                    Month
                  </label>
                  <select
                    className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    value={month}
                    onChange={(e) => {
                      setMonth(e.target.value);
                      setOffset(0);
                    }}
                    disabled={monthOptions.length === 0}
                  >
                    {monthOptions.length === 0 && <option value="">No data available</option>}
                    {monthOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                        {o.isCurrent ? "  (current)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Product Code */}
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                    Product Code
                  </label>
                  <input
                    type="search"
                    className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    placeholder="Search product code..."
                    value={productCodeInput}
                    onChange={(e) => setProductCodeInput(e.target.value)}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                    Status
                  </label>
                  <select
                    className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setOffset(0);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                {/* Clear Filters button */}
                <div>
                  <button
                    className="btn btn-outline-primary p-2 w-full"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="p-3 w-full bg-white shadow rounded-lg">
            <DataTableComponent
              keyField={(row) => `${row.id}`}
              columns={columns}
              data={data}
              loading={rulesIsLoading}
              totalRows={totalRows}
              handlePerRowsChange={handlePerRowsChange}
              handlePageChange={handlePageChange}
            >
              <div className="border-b pb-3 border-gray-300 bg-gray-50 rounded-t-lg px-4 pt-3">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold text-gray-800">{data.length}</span> of{" "}
                    <span className="font-semibold text-gray-800">{totalRows}</span> rules
                  </div>
                  <div className="text-xs text-gray-500">
                    Sorted by <span className="font-semibold">Total Sale Value</span> (highest first)
                  </div>
                </div>
              </div>
            </DataTableComponent>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDateRankRules;

// import React, { useEffect, useMemo, useState, useCallback } from "react";
// import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
// import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
// import { useFetchProductDateRankRulesMutation } from "../../../features/tenant/rankRules/productDateRankRulesApi";
// import toast from "react-hot-toast";
// import { formatter } from "../../../utils/numberFormatter";

// // ── Config ───────────────────────────────────────────────────────────────────
// // Hardcoded FY list (add new years here as they roll in)
// const FINANCIAL_YEARS = ["2026-2027", "2025-2026"];
// const DEFAULT_FY = "2026-2027";

// const MONTH_NAMES = [
//   "April", "May", "June", "July", "August", "September",
//   "October", "November", "December", "January", "February", "March",
// ];
// // FY month order: April = idx 0 ... March = idx 11
// // Real calendar month (1-12) for each FY index
// const FY_MONTH_TO_CAL = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

// // Given an FY string ("2026-2027"), return the calendar year for FY index i
// const calYearForFyIndex = (fy, fyIdx) => {
//   const [startY] = fy.split("-").map(Number);
//   // April(0)–December(8) → startY, January(9)–March(11) → startY + 1
//   return fyIdx <= 8 ? startY : startY + 1;
// };

// // Build list of month options for a given FY
// // If it's the current FY, only include months that have started (today or earlier)
// const buildMonthOptions = (fy) => {
//   const today = new Date();
//   const currentCalYear = today.getFullYear();
//   const currentCalMonth = today.getMonth() + 1; // 1-12

//   const options = [];
//   for (let fyIdx = 0; fyIdx < 12; fyIdx++) {
//     const calMonth = FY_MONTH_TO_CAL[fyIdx];
//     const calYear = calYearForFyIndex(fy, fyIdx);

//     // If this month is in the future relative to today → skip
//     const isFuture =
//       calYear > currentCalYear ||
//       (calYear === currentCalYear && calMonth > currentCalMonth);

//     if (isFuture) continue;

//     options.push({
//       value: `${calYear}-${String(calMonth).padStart(2, "0")}`,
//       label: `${MONTH_NAMES[fyIdx]} ${calYear}`,
//       isCurrent: calYear === currentCalYear && calMonth === currentCalMonth,
//     });
//   }
//   return options;
// };

// const ProductDateRankRules = () => {
//   const [
//     fetchProductDateRankRules,
//     { data: rulesData, isLoading: rulesIsLoading, error: rulesError },
//   ] = useFetchProductDateRankRulesMutation();

//   const [totalRows, setTotalRows] = useState(0);
//   const [data, setData] = useState([]);
//   const [limit, setLimit] = useState(30);
//   const [offset, setOffset] = useState(0);

//   // Filters — the ones actually applied (what we send to API)
//   const [financialYear, setFinancialYear] = useState(DEFAULT_FY);
//   const [month, setMonth] = useState(""); // set after mount to current month
//   const [productCodeInput, setProductCodeInput] = useState(""); // what user types
//   const [productCode, setProductCode] = useState(""); // debounced value that hits API
//   const [status, setStatus] = useState("all");

//   // Month options for the currently selected FY
//   const monthOptions = useMemo(() => buildMonthOptions(financialYear), [financialYear]);

//   // Initialize month to "current" the first time options are ready
//   useEffect(() => {
//     if (!month && monthOptions.length > 0) {
//       const current = monthOptions.find((o) => o.isCurrent) || monthOptions[monthOptions.length - 1];
//       setMonth(current.value);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [monthOptions]);

//   // If FY changes and current month isn't in new options → auto-pick a valid one
//   useEffect(() => {
//     if (monthOptions.length === 0) {
//       setMonth("");
//       return;
//     }
//     const stillValid = monthOptions.some((o) => o.value === month);
//     if (!stillValid) {
//       const current = monthOptions.find((o) => o.isCurrent) || monthOptions[monthOptions.length - 1];
//       setMonth(current.value);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [financialYear]);

//   // Debounce productCode: only trigger API 400ms after user stops typing
//   useEffect(() => {
//     const t = setTimeout(() => {
//       setProductCode(productCodeInput.trim());
//       setOffset(0);
//     }, 400);
//     return () => clearTimeout(t);
//   }, [productCodeInput]);

//   // Fetch whenever a filter (or pagination) changes
//   const fetchRulesFunc = useCallback(() => {
//     if (!month) return; // skip until month initialized
//     fetchProductDateRankRules({
//       token: sessionStorage.getItem("access_token"),
//       tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
//       limit,
//       offset,
//       body: {
//         product_code: productCode,
//         financial_year: financialYear,
//         month,
//         status,
//         sort_by: "total_sale_value",
//         sort_order: "desc",
//       },
//     });
//   }, [fetchProductDateRankRules, limit, offset, productCode, financialYear, month, status]);

//   useEffect(() => {
//     fetchRulesFunc();
//   }, [fetchRulesFunc]);

//   useEffect(() => {
//     if (rulesData?.body) {
//       setData(rulesData.body.data || []);
//       setTotalRows(rulesData.body.pagination?.total_rows || 0);
//     }
//     if (rulesError) {
//       toast.error("Something went wrong", { id: "rulesError" });
//       console.error("Product date rank rules error:", rulesError);
//     }
//   }, [rulesData, rulesError]);

//   const clearFilters = () => {
//     setFinancialYear(DEFAULT_FY);
//     setProductCodeInput("");
//     setProductCode("");
//     setStatus("all");
//     setOffset(0);
//     // month will auto-reset via FY effect
//   };

//   const formatDate = useCallback((d) => {
//     if (!d) return "N/A";
//     try {
//       return new Date(d).toLocaleDateString("en-IN", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//       });
//     } catch {
//       return d;
//     }
//   }, []);

//   // Columns memoized so DataTable doesn't re-render unnecessarily
//   const columns = useMemo(
//     () => [
//       {
//         name: "SNo",
//         cell: (row, idx) => offset + Number(idx + 1),
//         width: "70px",
//       },
//       {
//         name: "Product Code",
//         selector: (row) => row.product_code || "N/A",
//         sortable: true,
//         width: "180px",
//         cell: (row) => (
//           <span className="font-semibold text-gray-800">{row.product_code || "N/A"}</span>
//         ),
//       },
//       {
//         name: "Total Sale Value",
//         selector: (row) => row.total_sale_value || 0,
//         sortable: true,
//         width: "180px",
//         right: true,
//         cell: (row) => (
//           <span className="font-semibold text-gray-800">
//             ₹ {formatter.format(row.total_sale_value || 0)}
//           </span>
//         ),
//       },
//       {
//         name: "Start Date",
//         selector: (row) => row.start_date,
//         sortable: true,
//         width: "130px",
//         cell: (row) => formatDate(row.start_date),
//       },
//       {
//         name: "End Date",
//         selector: (row) => row.end_date,
//         sortable: true,
//         width: "140px",
//         cell: (row) =>
//           row.is_ongoing ? (
//             <span className="text-blue-700 font-medium">Ongoing</span>
//           ) : (
//             formatDate(row.end_date)
//           ),
//       },
//       {
//         name: "Duration",
//         selector: (row) => row.duration_days || 0,
//         sortable: true,
//         width: "110px",
//         cell: (row) => (
//           <span className="text-gray-600">
//             {row.duration_days} {row.duration_days === 1 ? "day" : "days"}
//           </span>
//         ),
//       },
//       {
//         name: "Rank",
//         selector: (row) => row.rank,
//         width: "90px",
//         center: true,
//         cell: (row) => (
//           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
//             {Number(row.rank).toFixed(2)}
//           </span>
//         ),
//       },
//       {
//         name: "State",
//         cell: (row) => {
//           const styles = {
//             Active: "bg-green-100 text-green-800",
//             Ended: "bg-gray-200 text-gray-700",
//             Upcoming: "bg-blue-100 text-blue-800",
//           };
//           return (
//             <span
//               className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
//                 styles[row.state] || "bg-gray-100 text-gray-700"
//               }`}
//             >
//               {row.state}
//             </span>
//           );
//         },
//         width: "110px",
//       },
//       {
//         name: "Status",
//         cell: (row) => (
//           <span
//             className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
//               row.status === "1" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
//             }`}
//           >
//             {row.status === "1" ? "Active" : "Inactive"}
//           </span>
//         ),
//         width: "100px",
//       },
//       {
//         name: "Updated",
//         selector: (row) => row.updated_at,
//         sortable: true,
//         width: "140px",
//         cell: (row) => <span className="text-gray-600 text-xs">{formatDate(row.updated_at)}</span>,
//       },
//     ],
//     [offset, formatDate]
//   );

//   const handlePageChange = useCallback(
//     (pageNumber) => setOffset((pageNumber - 1) * limit),
//     [limit]
//   );

//   const handlePerRowsChange = useCallback((newPerPage) => {
//     setLimit(newPerPage);
//     setOffset(0);
//   }, []);

//   const hasActiveFilters =
//     productCode || status !== "all" || financialYear !== DEFAULT_FY;

//   const selectedMonthLabel = useMemo(
//     () => monthOptions.find((o) => o.value === month)?.label || "",
//     [monthOptions, month]
//   );

//   return (
//     <div className="flex w-full bg-gray-200 min-h-screen justify-center">
//       <div className="w-full">
//         {/* Header */}
//         <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
//           High GP Product Rules
//           <LinksComponent />
//         </div>

//         <div className="p-3">
//           {/* Filter Card */}
//           <div className="bg-white shadow rounded-lg mb-3 border border-gray-200">
//             <div className="border-b border-gray-300 bg-sky-50 rounded-t-lg px-4 py-2 flex items-center justify-between">
//               <div className="flex items-center gap-2">
//                 <span className="text-sm font-semibold text-gray-700">Filters</span>
//                 {hasActiveFilters && (
//                   <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
//                     active
//                   </span>
//                 )}
//               </div>
//               {selectedMonthLabel && (
//                 <span className="text-xs text-gray-500">
//                   Viewing:&nbsp;
//                   <span className="font-semibold text-gray-700">{selectedMonthLabel}</span>
//                 </span>
//               )}
//             </div>

//             <div className="p-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
//                 {/* Financial Year */}
//                 <div>
//                   <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
//                     Financial Year
//                   </label>
//                   <select
//                     className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
//                     value={financialYear}
//                     onChange={(e) => {
//                       setFinancialYear(e.target.value);
//                       setOffset(0);
//                     }}
//                   >
//                     {FINANCIAL_YEARS.map((fy) => (
//                       <option key={fy} value={fy}>
//                         {fy}
//                       </option>
//                     ))}
//                   </select>
//                 </div>

//                 {/* Month */}
//                 <div>
//                   <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
//                     Month
//                   </label>
//                   <select
//                     className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
//                     value={month}
//                     onChange={(e) => {
//                       setMonth(e.target.value);
//                       setOffset(0);
//                     }}
//                     disabled={monthOptions.length === 0}
//                   >
//                     {monthOptions.length === 0 && <option value="">No data available</option>}
//                     {monthOptions.map((o) => (
//                       <option key={o.value} value={o.value}>
//                         {o.label}
//                         {o.isCurrent ? "  (current)" : ""}
//                       </option>
//                     ))}
//                   </select>
//                 </div>

//                 {/* Product Code */}
//                 <div>
//                   <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
//                     Product Code
//                   </label>
//                   <input
//                     type="search"
//                     className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
//                     placeholder="Search product code..."
//                     value={productCodeInput}
//                     onChange={(e) => setProductCodeInput(e.target.value)}
//                   />
//                 </div>

//                 {/* Status */}
//                 <div>
//                   <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
//                     Status
//                   </label>
//                   <select
//                     className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
//                     value={status}
//                     onChange={(e) => {
//                       setStatus(e.target.value);
//                       setOffset(0);
//                     }}
//                   >
//                     <option value="all">All</option>
//                     <option value="1">Active</option>
//                     <option value="0">Inactive</option>
//                   </select>
//                 </div>

//                 {/* Clear Filters button */}
//                 <div>
//                   <button
//                     className="btn btn-outline-primary p-2 w-full"
//                     onClick={clearFilters}
//                     disabled={!hasActiveFilters}
//                   >
//                     Clear Filters
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Data Table */}
//           <div className="p-3 w-full bg-white shadow rounded-lg">
//             <DataTableComponent
//               keyField={(row) => `${row.id}`}
//               columns={columns}
//               data={data}
//               loading={rulesIsLoading}
//               totalRows={totalRows}
//               handlePerRowsChange={handlePerRowsChange}
//               handlePageChange={handlePageChange}
//             >
//               <div className="border-b pb-3 border-gray-300 bg-gray-50 rounded-t-lg px-4 pt-3">
//                 <div className="flex justify-between items-center">
//                   <div className="text-sm text-gray-600">
//                     Showing{" "}
//                     <span className="font-semibold text-gray-800">{data.length}</span> of{" "}
//                     <span className="font-semibold text-gray-800">{totalRows}</span> rules
//                   </div>
//                   <div className="text-xs text-gray-500">
//                     Sorted by <span className="font-semibold">Total Sale Value</span> (highest first)
//                   </div>
//                 </div>
//               </div>
//             </DataTableComponent>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductDateRankRules;
